import { createModuleLogger } from '@utils/logger';

const log = createModuleLogger('PlazaMultiSourceSearch');
import { PLAZA_MUSIC_SOURCES } from './plazaSourceRegistry.js';
import qqPluginClient from './qqPluginClient.js';
import wyPluginClient from './wyPluginClient.js';
import kwPluginClient from './kwPluginClient.js';
import kgPluginClient from './kgPluginClient.js';
import mgPluginClient from './mgPluginClient.js';
import lxQqPluginClient from './lxQqPluginClient.js';
import lxKwPluginClient from './lxKwPluginClient.js';
import lxKgPluginClient from './lxKgPluginClient.js';
import lxWyPluginClient from './lxWyPluginClient.js';

const CLIENT_MAP = {
  qq: qqPluginClient,
  wy: wyPluginClient,
  kw: kwPluginClient,
  kg: kgPluginClient,
  mg: mgPluginClient,
  'lx-qq': lxQqPluginClient,
  'lx-kw': lxKwPluginClient,
  'lx-kg': lxKgPluginClient,
  'lx-wy': lxWyPluginClient
};

function filterSongsByArtist(songs, filterByArtist) {
  if (!filterByArtist?.trim()) {
    return songs;
  }
  const keyword = filterByArtist.trim().toLowerCase();
  return songs.filter((song) => (song.artist || '').toLowerCase().includes(keyword));
}

/**
 * 按注册表顺序依次尝试各音源，直到有结果或全部失败。
 * @param {string} query
 * @param {{ page?: number, filterByArtist?: string|null, fixedSourceId?: string|null }} options
 */
export async function searchPlazaWithFallback(query, options = {}) {
  const { page = 1, filterByArtist = null, fixedSourceId = null } = options;
  const triedSources = [];
  const errors = [];

  // autoFallback === false 的音源（如「长青X源」）不参与未指定音源时的自动轮询，
  // 因为它们复用与兄弟音源相同的搜索结果，仅播放解析后端不同，自动轮询意义不大，
  // 只在用户手动点击切换（fixedSourceId）时生效
  const sources = fixedSourceId
    ? PLAZA_MUSIC_SOURCES.filter((item) => item.id === fixedSourceId && item.enabled)
    : PLAZA_MUSIC_SOURCES.filter((item) => item.enabled && item.autoFallback !== false);

  if (sources.length === 0) {
    throw new Error('没有可用的广场搜索音源');
  }

  for (const source of sources) {
    const client = CLIENT_MAP[source.id];
    if (!client?.search) {
      continue;
    }

    triedSources.push(source.id);

    try {
      log.debug(`[广场搜索] 尝试音源 ${source.name}(${source.id})`, { query, page });
      const result = await client.search(query, page, 'music');
      const rawSongs = result?.data || [];
      const filteredSongs = filterSongsByArtist(rawSongs, filterByArtist);

      if (filteredSongs.length === 0) {
        log.debug(`[广场搜索] 音源 ${source.name} 无结果，尝试下一个`, { query, page });
        continue;
      }

      log.info(`[广场搜索] 音源 ${source.name} 命中 ${filteredSongs.length} 条`, { query, page });

      return {
        songs: filteredSongs,
        totalSongs: result.total ?? filteredSongs.length,
        isEnd: result.isEnd ?? false,
        sourceId: source.id,
        sourceName: source.name,
        searchType: source.searchType,
        triedSources
      };
    } catch (error) {
      const message = error?.message || String(error);
      errors.push({ sourceId: source.id, sourceName: source.name, message });
      log.warn(`[广场搜索] 音源 ${source.name} 失败: ${message}`, { query, page });
    }
  }

  return {
    songs: [],
    totalSongs: 0,
    isEnd: true,
    sourceId: null,
    sourceName: null,
    searchType: null,
    triedSources,
    errors
  };
}

export function getPlazaPluginClient(sourceId) {
  return CLIENT_MAP[sourceId] || null;
}

export function getPlazaPluginClientBySearchType(searchType) {
  const source = PLAZA_MUSIC_SOURCES.find((item) => item.searchType === searchType);
  return source ? CLIENT_MAP[source.id] : null;
}

export const PLAZA_PLUGIN_SEARCH_TYPES = PLAZA_MUSIC_SOURCES.map((item) => item.searchType);

export function isPlazaPluginSearchType(searchType) {
  return PLAZA_PLUGIN_SEARCH_TYPES.includes(searchType);
}

export function enrichPlazaSearchSong(song, page, isEnd, sourceMeta = {}) {
  return {
    ...song,
    isExternal: true,
    isExternalWithResources: true,
    source: song.source || sourceMeta.searchType || 'qq-plugin',
    externalMeta: {
      platform: sourceMeta.id || song.externalMeta?.platform || 'unknown',
      plugin: sourceMeta.name || song.externalMeta?.plugin || 'unknown',
      version: sourceMeta.version || song.externalMeta?.version || 'unknown',
      page,
      isEnd
    }
  };
}
